import React, { useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Button, CloseIcon, Tooltip } from 'examkrackers-components'
import * as R from 'ramda'
import {
  COURSE_TYPES,
  DAYS_TO_PROMPT_SUBSCRIPTION,
  EXTEND_SUBSCRIPTION_URL,
  getCourseRemainingDays
} from 'utils/courses'
import styled from 'styled-components'
import { getCurrentCourse } from 'modules/courses/ducks/selectors'

const PlainButton = ({ handleClick, disabled = false }) => {
  const { t } = useTranslation()
  return (
    <Button
      data-testid='ReorganizeCalendarModal-Button-reorganize'
      variant='contained'
      color='secondary'
      onClick={handleClick}
      disabled={disabled}
    >
      {t('calendar.button.reorganize')}
    </Button>
  )
}

const FreeTrialTooltip = ({ children }) => {
  const currentCourse = useSelector(getCurrentCourse)

  return (
    <Tooltip
      data-testid='ReorganizeCalendarModal-Tooltip-reorganize-isFreeTrial'
      tooltipContent='Once you purchase KrackU, this button will allow you to reorganize your calendar.'
      id={`reorganize-${currentCourse.id}`}
    >
      {children}
    </Tooltip>
  )
}

const ExtendSubscriptionPopup = () => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)
  const { t } = useTranslation()
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  return (
    <PopupContainer>
      <PlainButton handleClick={handleOpen} />
      <Popup ref={containerRef} open={open}>
        <CloseIcon onClick={handleClose} />
        <p>{t('calendar.reorganizeSubscriptionPropmt.description')}</p>
        <Button
          data-testid='prompt-extend-subscription-button'
          variant='contained'
          color='tertiary'
          onClick={() => {
            window.open(EXTEND_SUBSCRIPTION_URL, '_blank')
          }}
        >
          {t('calendar.reorganizeSubscriptionPropmt.extend')}
        </Button>
      </Popup>
    </PopupContainer>
  )
}

export const ReorganiseCalendarButton = ({ handleOpen }) => {
  const currentCourse = useSelector(getCurrentCourse)
  const isFreeTrial = R.pipe(
    R.propOr('', 'type'),
    R.equals(COURSE_TYPES.freeTrial)
  )(currentCourse)

  const remainingDays = getCourseRemainingDays(currentCourse)

  const shouldPromptSubscription = remainingDays <= DAYS_TO_PROMPT_SUBSCRIPTION
  switch (true) {
    case shouldPromptSubscription:
      // Temporarily disabled subscription prompt popup
      // TODO: Uncomment when client decides to restore this functionality
      // return <ExtendSubscriptionPopup />
      return <FreeTrialTooltip>
          <PlainButton handleClick={handleOpen} disabled={true} />
        </FreeTrialTooltip>
    case isFreeTrial:
      return (
        <FreeTrialTooltip>
          <PlainButton handleClick={handleOpen} disabled={true} />
        </FreeTrialTooltip>
      )
    default:
      return <PlainButton handleClick={handleOpen} />
  }
}

export default ReorganiseCalendarButton

const PopupContainer = styled.div`
  position: relative;
`

const Popup = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? 'flex' : 'none')};
  position: absolute;
  top: calc(100% + 5px);
  left: -55px;
  color: ${({ theme }) => theme.colors.main.white};
  background-color: ${({ theme }) => theme.colors.main.text};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  min-width: 297px;
  min-height: 124px;
  border-radius: 4px;
  padding: 16px;
  z-index: ${({ theme }) => theme.zIndex.modal};

  svg {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
  }

  p {
    font-weight: 400;
    font-size: 11px;
  }

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 65px;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${({ theme }) => theme.colors.main.text};
  }
`
